/**
 * @fileoverview Which parts of the app each permission controls, and how groups are ordered.
 * @description The permission tables only say what a permission is; the Capabilities page also says
 * what it is for in the Sales app (the "Dashboard · View" line under a permission's name). That
 * link is a fixed fact about this app's own screens, so it lives here: each permission code maps to
 * the app actions it relates to, ordered as the RBAC matrix lists them. A permission engineering
 * adds later has no entry and simply shows no usage line (and is hidden by the "Sales app" filter).
 * Display names are translation keys built from the codes (see capability-label.utils.ts).
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

/** Groups in the order the page lists them; a group not listed sorts after these, by name. */
export const CAPABILITY_GROUP_ORDER: readonly string[] = [
  'TENANT',
  'USER',
  'USER_MANAGEMENT',
  'SALES_CRM',
  'APPROVAL',
  'DASHBOARD',
  'REPORT',
  'AUDIT',
  'AUTHORIZATION',
  'SESSION',
  'DEVICE',
  'NOTIFICATION',
  'DATA_INGESTION',
] as const

/** Permission code -> the app actions it relates to (each has a `capabilities.controls.*` label). */
export const PERMISSION_CONTROLS: Readonly<Record<string, readonly string[]>> = {
  'DASHBOARD.READ': ['dash.view'],
  'USER.INVITE': [
    'dash.addUser',
    'users.addHead',
    'users.addManager',
    'users.addExec',
    'users.invite',
    'clients.inviteHr',
  ],
  'USER.READ': ['users.view'],
  'USER_MANAGEMENT.TEAM.READ': ['users.view', 'team.view', 'team.details', 'team.banks'],
  'USER.UPDATE': ['users.edit', 'users.changeManager', 'users.reactivate'],
  'AUTHORIZATION.ASSIGNMENT.CREATE': ['users.changeRole'],
  'USER.DEACTIVATE': ['users.deactivate'],
  'TENANT.READ': ['pipeline.view', 'clients.view', 'clients.profile'],
  'TENANT.CREATE': ['pipeline.create'],
  'APPROVAL.DECIDE': ['pipeline.approve', 'reassign.approve'],
  'SALES_CRM.PROSPECT.ASSIGN': [
    'pipeline.select',
    'target.assignMgr',
    'target.assignExec',
    'reassign.any',
    'reassign.team',
    'reassign.stepIn',
    'reassign.closed',
    'reassign.approve',
  ],
  'REPORT.READ': ['perf.view', 'perf.ranking', 'perf.pending'],
  'REPORT.EXPORT': ['perf.export'],
  'TENANT.UPDATE': ['clients.edit', 'clients.archive', 'org.manage'],
  'AUDIT.READ': ['audit.read'],
  'AUTHORIZATION.PERMISSION.GRANT': ['cap.manage'],
  'AUTHORIZATION.PERMISSION.REVOKE': ['cap.manage'],
}
